// src/components/support/Composer.jsx
//
// Reply box at the bottom of the ticket drawer. Staff get two extras:
// "Staff-only note" (never shown or sent to the client) and "then set status",
// so a reply and a status change go out as one action and one notification.

import React, { useRef, useState } from 'react';
import { MAX_IMAGES, imageToDataUrl } from './supportShared';

// Optional status change sent with the reply, so the client gets one
// notification ("replied, and it's resolved") instead of two.
const STAFF_NEXT = [
  { id: '', label: 'Status: no change' },
  { id: 'in_progress', label: 'Status → In progress' },
  { id: 'waiting_client', label: 'Status → Waiting on client' },
  { id: 'resolved', label: 'Status → Resolved' },
];

export default function Composer({ scope, onSend, disabled, placeholder }) {
  const [body, setBody] = useState('');
  const [images, setImages] = useState([]);
  const [internal, setInternal] = useState(false);
  const [nextStatus, setNextStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const addFiles = async (files) => {
    setError(null);
    const room = MAX_IMAGES - images.length;
    if (room <= 0) return setError(`Attach up to ${MAX_IMAGES} images.`);
    try {
      const picked = await Promise.all(Array.from(files).slice(0, room).map((f) => imageToDataUrl(f)));
      setImages((prev) => [...prev, ...picked]);
    } catch (e) {
      setError(e.message);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!body.trim()) return setError('Write a message first.');
    setBusy(true);
    setError(null);
    try {
      await onSend({
        body: body.trim(),
        attachments: images,
        ...(scope === 'admin' ? { internal, status: internal ? undefined : nextStatus || undefined } : {}),
      });
      setBody('');
      setImages([]);
      setNextStatus('');
      setInternal(false);
    } catch (e) {
      setError(e.message || 'Message not sent. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`sp-composer ${internal ? 'is-internal' : ''}`}>
      {scope === 'admin' && (
        <div className="sp-composer-mode" role="tablist" aria-label="Message type">
          <button type="button" role="tab" aria-selected={!internal} className={!internal ? 'active' : ''} onClick={() => setInternal(false)}>
            Reply to client
          </button>
          <button type="button" role="tab" aria-selected={internal} className={internal ? 'active' : ''} onClick={() => setInternal(true)}>
            <i className="fas fa-lock"></i> Staff-only note
          </button>
        </div>
      )}
      <label className="visually-hidden" htmlFor={`sp-compose-${scope}`}>Message</label>
      <textarea
        id={`sp-compose-${scope}`}
        className="sp-textarea"
        rows={3}
        maxLength={5000}
        value={body}
        disabled={disabled || busy}
        placeholder={internal ? 'Only your team sees this.' : placeholder}
        onChange={(e) => { setBody(e.target.value); setError(null); }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit(); }}
      />
      {images.length > 0 && (
        <div className="sp-atts sp-atts-pending">
          {images.map((img, i) => (
            <div key={i} className="sp-att">
              <img src={img.data} alt={img.name} />
              <button type="button" className="sp-att-remove" aria-label={`Remove ${img.name}`}
                      onClick={() => setImages((prev) => prev.filter((_, j) => j !== i))}>
                <i className="fas fa-times"></i>
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <div className="bl-error-text sp-composer-error" role="alert">{error}</div>}
      <div className="sp-composer-bar">
        <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => addFiles(e.target.files)} />
        <button type="button" className="bl-btn-ghost" onClick={() => fileRef.current?.click()}
                disabled={disabled || busy || images.length >= MAX_IMAGES} aria-label="Attach images" title="Attach images">
          <i className="fas fa-paperclip"></i>
        </button>
        {scope === 'admin' && !internal && (
          <select className="bl-select sp-next-status" value={nextStatus} onChange={(e) => setNextStatus(e.target.value)} aria-label="Status after sending">
            {STAFF_NEXT.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        )}
        <button type="button" className="bl-btn-primary bl-btn-sm sp-send" onClick={submit} disabled={disabled || busy}>
          {busy ? 'Sending…' : internal ? 'Add note' : 'Send'}
        </button>
      </div>
    </div>
  );
}
